import type { AppList, FieldType } from '@/types/platform';

// The editor's own copy of the design. A list or field that already exists keeps its key;
// a new one has no key until it is worked out from what the person typed.

export interface EditorField {
  id: string;
  savedKey: string | null;
  label: string;
  type: FieldType;
  required: boolean;
  // Choices as typed, separated by commas.
  optionsText: string;
}

export interface EditorList {
  id: string;
  savedKey: string | null;
  name: string;
  singular: string;
  titleFieldId: string | null;
  fields: EditorField[];
}

export interface ResolvedField extends EditorField {
  key: string;
}

export interface ResolvedList extends Omit<EditorList, 'fields'> {
  key: string;
  fields: ResolvedField[];
}

export interface ReservedKeys {
  lists: string[];
  fields: string[];
}

export interface Removals {
  lists: string[];
  fields: { list: string; fields: string[] }[];
}

const KEY_LIMIT = 40;

// The field types the server lets stand as a record's title.
const TITLE_TYPES: FieldType[] = ['text', 'email', 'phone'];

export function toEditorLists(lists: AppList[]): EditorList[] {
  return lists.map((list) => {
    const fields = list.fields.map((field) => ({
      id: `field-${list.key}-${field.key}`,
      savedKey: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      optionsText: field.options.join(', '),
    }));

    return {
      id: `list-${list.key}`,
      savedKey: list.key,
      name: list.name,
      singular: list.singular,
      titleFieldId: fields.find((field) => field.savedKey === list.titleField)?.id ?? null,
      fields,
    };
  });
}

// Lowercase letters, numbers, and underscores, starting with a letter, at most 40 long.
function keyFrom(text: string, fallback: string): string {
  const cleaned = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  let key = fallback;
  if (/^[a-z]/.test(cleaned)) {
    key = cleaned;
  } else if (cleaned !== '') {
    key = `${fallback}_${cleaned}`;
  }

  return key.slice(0, KEY_LIMIT).replace(/_+$/, '');
}

function uniqueKey(base: string, taken: Set<string>): string {
  let candidate = base;
  let count = 2;

  while (taken.has(candidate)) {
    const suffix = `_${count}`;
    candidate = `${base.slice(0, KEY_LIMIT - suffix.length)}${suffix}`;
    count += 1;
  }

  return candidate;
}

function savedKeys(items: { savedKey: string | null }[]): string[] {
  return items.flatMap((item) => (item.savedKey === null ? [] : [item.savedKey]));
}

// Gives every list and field its key. A new key is unique where it sits and never a reserved name.
export function resolveKeys(lists: EditorList[], reserved: ReservedKeys): ResolvedList[] {
  const takenLists = new Set([...reserved.lists, ...savedKeys(lists)]);

  return lists.map((list) => {
    const key = list.savedKey ?? uniqueKey(keyFrom(list.name, 'list'), takenLists);
    takenLists.add(key);

    const takenFields = new Set([...reserved.fields, ...savedKeys(list.fields)]);
    const fields = list.fields.map((field) => {
      const fieldKey = field.savedKey ?? uniqueKey(keyFrom(field.label, 'field'), takenFields);
      takenFields.add(fieldKey);

      return { ...field, key: fieldKey };
    });

    return { ...list, key, fields };
  });
}

export function titleCandidates(list: ResolvedList): ResolvedField[] {
  return list.fields.filter((field) => TITLE_TYPES.includes(field.type));
}

// The server's rule: the chosen field if it can be a title, else the first that can, else the first.
export function titleField(list: ResolvedList): ResolvedField | null {
  const candidates = titleCandidates(list);

  return (
    candidates.find((field) => field.id === list.titleFieldId) ??
    candidates[0] ??
    list.fields[0] ??
    null
  );
}

function parseOptions(text: string): string[] {
  return text
    .split(',')
    .map((option) => option.trim())
    .filter((option) => option !== '');
}

// What the design update action reads.
export function toPayload(lists: ResolvedList[]) {
  return {
    lists: lists.map((list) => ({
      key: list.key,
      name: list.name.trim(),
      singular: list.singular.trim(),
      title_field: titleField(list)?.key ?? null,
      fields: list.fields.map((field) => ({
        key: field.key,
        label: field.label.trim(),
        type: field.type,
        required: field.required,
        options: field.type === 'choice' ? parseOptions(field.optionsText) : [],
      })),
    })),
  };
}

// Lists and fields that are saved now and gone from the editor.
export function removalsFrom(saved: AppList[], lists: ResolvedList[]): Removals {
  const removals: Removals = { lists: [], fields: [] };

  saved.forEach((savedList) => {
    const current = lists.find((list) => list.key === savedList.key);

    if (current) {
      const keys = new Set(current.fields.map((field) => field.key));
      const gone = savedList.fields.filter((field) => !keys.has(field.key));

      if (gone.length > 0) {
        removals.fields.push({ list: current.name, fields: gone.map((field) => field.label) });
      }
    } else {
      removals.lists.push(savedList.name);
    }
  });

  return removals;
}
